import { Selection, EnterElement, ScaleBand, range, scaleLinear, format, axisRight } from "d3"
import { D3OneToOneRenderable } from "../../../D3/D3OneToOneRenderable"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"
import { SpectrogramTraceConfigJSON } from "../../../../Types/Trace"
import { ColorSpectrum, getSpectrumInterpolator, DISCRETE_RGB_COLORS } from "../../../../Types/ColorSpectrum"
import { MobergFontFamily } from "../../../../../../../../Moberg"
import { getNiceTicks, getTickYCorrection } from "../../../D3/axisUtils"
import { AXIS_TICK_FONT_SIZE, MODALITY_LABEL_FONT_SIZE } from "../../Constants"

type D3SpectrogramTraceLegendConfig = {
	tracePositionScale: ScaleBand<string>
	trace: SpectrogramTraceConfigJSON
	graphId: string
	graphWidth: number
}

export class D3SpectrogramTraceLegend extends D3OneToOneRenderable<SVGGElement, SVGGElement, D3SpectrogramTraceLegendConfig, ModalityGraphGroupReactCallbacks> {
	private gap = 8
	private width = 16
	private labelXOffset = 2
	private textYOffset = -16
	private gradientScale = scaleLinear()
	private colorScaleAxisClassName = "d3-spectrogram-color-scale-axis"
	private labelClassName = "d3-spectrogram-legend-label"

	private getGradientId(): string {
		const safeTraceId = this.config.trace.id.replace(/[^a-zA-Z0-9-_]/g, "_")
		return `spectrogram-${this.config.trace.colorSpectrum}-${safeTraceId}`
	}

	constructor(root: SVGGElement, config: D3SpectrogramTraceLegendConfig, className: string, reactCallbacks: ModalityGraphGroupReactCallbacks) {
		super(root, config, className, reactCallbacks)
		this.updateDerivedState()
		this.render()
	}

	protected updateDerivedState(): void {
		this.gradientScale
			.domain([this.config.trace.lowerBound, this.config.trace.upperBound])
			.range([this.config.tracePositionScale.bandwidth() - 1, 0])
	}

	protected enter(newElements: Selection<EnterElement, D3SpectrogramTraceLegendConfig, any, any>): Selection<SVGGElement, D3SpectrogramTraceLegendConfig, SVGGElement, any> {
		const legend = newElements
			.append("g")
			.attr("class", this.className)
			.attr("transform", `translate(0, ${this.config.tracePositionScale(this.config.trace.id)})`)
			.style("color", this.getTraceColor)

		const colorBarX = this.config.graphWidth + this.gap

		const legendSVG = legend
			.append("svg")
			.attr("x", colorBarX)
			.attr("width", this.width)
			.attr("height", this.config.tracePositionScale.bandwidth())

		const legendDefs = legendSVG.append("defs")

		this.createGradient(legendDefs, this.config.trace.colorSpectrum)

		legendSVG
			.append("rect")
			.attr("fill", `url(#${this.getGradientId()})`)
			.attr("width", this.width)
			.attr("height", this.config.tracePositionScale.bandwidth())

		const colorScaleAxis = legend
			.append("g")
			.attr("class", this.colorScaleAxisClassName)
			.attr("transform", `translate(${colorBarX + this.width}, 0)`)

		this.renderColorScaleAxis(colorScaleAxis)

		colorScaleAxis.select(".domain").attr("stroke", "transparent")

		const foreignObject = legend
			.append("foreignObject")
			.attr("width", 250)
			.attr("height", 20)
			.attr("transform", `translate(${this.labelXOffset}, ${-this.textYOffset - 20})`)

		foreignObject
			.append<HTMLButtonElement>("xhtml:button")
			.style("border", 0)
			.style("background", "var(--background-color)")
			.style("max-width", "240px")
			.style("color", "var(--text-color)")
			.style("padding-left", "4px")
			.style("padding-right", "8px")
			.attr("class", this.labelClassName)
			.style("font-family", MobergFontFamily.REGULAR)
			.style("font-weight", "bold")
			.style("font-size", MODALITY_LABEL_FONT_SIZE)
			.style("white-space", "nowrap")
			.style("overflow", "hidden")
			.style("text-overflow", "ellipsis")
			.attr("title", this.config.trace.name)
			.text(this.config.trace.name)
			.on("mousedown", this.onMouseDown)
			.on("contextmenu", this.onContextMenu)

		return legend
	}

	protected update(updatedElements: Selection<SVGGElement, D3SpectrogramTraceLegendConfig, any, any>): Selection<SVGGElement, D3SpectrogramTraceLegendConfig, SVGGElement, any> {
		const legend = updatedElements
			.attr("transform", `translate(0, ${this.config.tracePositionScale(this.config.trace.id)})`)
			.style("color", this.getTraceColor)

		const colorBarX = this.config.graphWidth + this.gap

		const legendSVG = legend.select("svg")
		const legendDefs = legendSVG.select("defs") as Selection<SVGDefsElement, any, any, any>

		this.createGradient(legendDefs, this.config.trace.colorSpectrum)

		legendSVG
			.attr("x", colorBarX)
			.attr("height", this.config.tracePositionScale.bandwidth())
			.select("rect").attr("height", this.config.tracePositionScale.bandwidth())

		legend
			.select("foreignObject")
			.attr("transform", `translate(${this.labelXOffset}, ${-this.textYOffset - 20})`)

		legend
			.select("." + this.labelClassName)
			.style("font-family", MobergFontFamily.REGULAR)
			.style("font-size", MODALITY_LABEL_FONT_SIZE)
			.style("color", "var(--text-color)")
			.text(this.config.trace.name)

		legendSVG.select("rect").attr("fill", `url(#${this.getGradientId()})`)

		const colorScaleAxis = legend.select<SVGGElement>("." + this.colorScaleAxisClassName)
			.attr("transform", `translate(${colorBarX + this.width}, 0)`)

		this.renderColorScaleAxis(colorScaleAxis)

		return legend
	}

	private renderColorScaleAxis(colorScaleAxis: Selection<SVGGElement, any, any, any>) {
		colorScaleAxis
			.call(
				axisRight(this.gradientScale)
					.tickValues(getNiceTicks(this.gradientScale))
					.tickFormat(format(".4~f"))
			)
			.style("font-size", AXIS_TICK_FONT_SIZE)
			.style("font-family", MobergFontFamily.REGULAR)

		colorScaleAxis.selectAll(".tick text")
			.attr("y", (d, i, nodes) => getTickYCorrection(i, nodes))

		colorScaleAxis.select(".domain").attr("stroke", "transparent")
	}

	private createGradient(defs: Selection<SVGDefsElement, any, any, any>, colorSpectrum: ColorSpectrum) {
		const gradientId = this.getGradientId()
		let gradient = defs.select<SVGLinearGradientElement>(`#${CSS.escape(gradientId)}`)

		if (gradient.empty()) {
			defs.selectChildren().remove()

			gradient = defs
				.append("linearGradient")
				.attr("id", gradientId)
				.attr("x1", "0%")
				.attr("x2", "0%")
				.attr("y1", "100%")
				.attr("y2", "0%")

			if (colorSpectrum === ColorSpectrum.DISCRETE_RGB) {
				const numColors = DISCRETE_RGB_COLORS.length
				DISCRETE_RGB_COLORS.forEach((color, index) => {
					const startOffset = (index / numColors) * 100
					const endOffset = ((index + 1) / numColors) * 100
					gradient.append("stop").attr("offset", `${startOffset}%`).attr("stop-color", color)
					gradient.append("stop").attr("offset", `${endOffset}%`).attr("stop-color", color)
				})
			} else {
				const interpolator = getSpectrumInterpolator(colorSpectrum)
				const colorStops = range(11).map(value => ({
					offset: `${Math.round((100 * value) / 10)}%`,
					color: interpolator(value / 10),
				}))

				colorStops.forEach(stop => {
					gradient.append("stop").attr("offset", stop.offset).attr("stop-color", stop.color)
				})
			}
		}

		return gradientId
	}

	private getTraceColor = (_config: D3SpectrogramTraceLegendConfig) => {
		return "var(--text-color)"
	}

	private onMouseDown = (event: MouseEvent, config: D3SpectrogramTraceLegendConfig) => {
		if (event.buttons === 1) {
			this.reactCallbacks.toggleTraceVisibility(this.config.graphId, config.trace.id)
		}
	}

	private onContextMenu = (event: React.MouseEvent, config: D3SpectrogramTraceLegendConfig) => {
		event.preventDefault()
		this.reactCallbacks.openTraceVisibilityContextMenu(event, config.trace, this.config.graphId)
	}
}
